import React from 'react';
import { motion } from 'framer-motion';
import { FaExclamationTriangle, FaPhone, FaClock, FaMapMarkerAlt } from 'react-icons/fa';

const EmergencyDental = () => {
  const emergencyServices = [
    {
      icon: <FaExclamationTriangle className="text-red-500 w-8 h-8" />,
      title: 'Severe Tooth Pain',
      description: 'Immediate relief for intense toothaches and dental emergencies',
    },
    {
      icon: <FaExclamationTriangle className="text-red-500 w-8 h-8" />,
      title: 'Broken Teeth',
      description: 'Emergency treatment for cracked, chipped, or broken teeth',
    },
    {
      icon: <FaExclamationTriangle className="text-red-500 w-8 h-8" />,
      title: 'Lost Fillings/Crowns',
      description: 'Quick replacement of lost dental restorations',
    },
    {
      icon: <FaExclamationTriangle className="text-red-500 w-8 h-8" />,
      title: 'Dental Trauma',
      description: 'Treatment for injuries to teeth, gums, or mouth',
    },
  ];

  return (
    <section className="bg-gradient-to-br from-red-50 via-white to-phoenix-beige py-20 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-red-100 rounded-full -translate-y-32 translate-x-32 opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-phoenix-gold/10 rounded-full translate-x-24 translate-y-24"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <FaExclamationTriangle className="text-red-500 w-8 h-8" />
          </motion.div>
          <h2 className="text-4xl lg:text-5xl font-bold text-phoenix-black mb-4">
            Dental <span className="text-red-500">Emergency</span> Care
          </h2>
          <p className="text-lg text-phoenix-gray max-w-3xl mx-auto mb-8">
            Don't wait when dental emergencies strike. Our team is here to provide immediate care
            when you need it most. Available 24/7 for urgent dental situations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {emergencyServices.map(({ icon, title, description }, index) => (
            <motion.div
              key={index}
              whileHover={{
                scale: 1.05,
                boxShadow: '0 20px 40px rgba(239, 68, 68, 0.15)'
              }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 text-center border border-red-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="mb-4 flex justify-center"
                whileHover={{ rotate: 10, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                {icon}
              </motion.div>
              <h3 className="text-xl font-bold text-phoenix-black mb-3">{title}</h3>
              <p className="text-phoenix-gray leading-relaxed">{description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-white rounded-2xl p-8 shadow-xl border border-red-100"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-phoenix-black mb-4">24/7 Emergency Contact</h3>
              <p className="text-phoenix-gray mb-6">
                Dental emergencies don't follow business hours. Our emergency hotline is available
                around the clock to handle urgent dental situations. Call immediately if you experience:
              </p>
              <ul className="space-y-2 text-phoenix-gray mb-6">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                  Severe tooth pain or swelling
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                  Knocked out or broken teeth
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                  Lost fillings or crowns
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                  Bleeding that won't stop
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <motion.div
                className="flex items-center p-4 bg-red-50 rounded-lg border border-red-200"
                whileHover={{ scale: 1.02 }}
              >
                <FaPhone className="text-red-500 w-6 h-6 mr-4" />
                <div>
                  <p className="font-semibold text-phoenix-black">Emergency Hotline</p>
                  <p className="text-red-600 font-bold text-lg">(+91) 9949042692</p>
                </div>
              </motion.div>
              <motion.div
                className="flex items-center p-4 bg-phoenix-beige rounded-lg border border-phoenix-gold/30"
                whileHover={{ scale: 1.02 }}
              >
                <FaClock className="text-phoenix-gold w-6 h-6 mr-4" />
                <div>
                  <p className="font-semibold text-phoenix-black">Available</p>
                  <p className="text-phoenix-gold font-bold">24 Hours a Day, 7 Days a Week</p>
                </div>
              </motion.div>
              <motion.div
                className="flex items-center p-4 bg-phoenix-beige rounded-lg border border-phoenix-gold/30"
                whileHover={{ scale: 1.02 }}
              >
                <FaMapMarkerAlt className="text-phoenix-gold w-6 h-6 mr-4" />
                <div>
                  <p className="font-semibold text-phoenix-black">Location</p>
                  <p className="text-phoenix-gold font-bold">Priyadarshini Colony, Chandrampalem, Srinivasa Nagar, Madhurawada, Visakhapatnam, Andhra Pradesh 530048</p>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EmergencyDental;
